import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createAllTimeRange, createTimeRange, fetchEarliestDate, formatDateForInput, getDefaultCustomDateRange } from '../../utils/dateUtils'

function TimeRangeSelector({
  value, // This is timeRange.duration from parent (e.g., 'allTime', 3600, 'custom')
  onChange,
  includeExtendedRanges = false,
  initialStartTime: propInitialStartTime, // From chart zoom, already formatted for <input type="datetime-local">
  initialEndTime: propInitialEndTime,     // From chart zoom, already formatted
  isExternallySetCustom,                   // True if 'custom' is due to chart zoom
  onExternalCustomProcessed, // New callback prop
}) {
  // State for the custom date/time input fields
  const [customStartTime, setCustomStartTime] = useState('')
  const [customEndTime, setCustomEndTime] = useState('')

  // Refs to store the previous prop values to detect changes
  const prevPropInitialStartTime = useRef()
  const prevPropInitialEndTime = useRef()

  // State for managing which option is selected in the dropdown and visibility of custom controls
  const [selectedValue, setSelectedValue] = useState(value)
  const [showCustomControls, setShowCustomControls] = useState(value === 'custom')

  const [earliestDate, setEarliestDate] = useState(null)

  // Fetch the earliest available date for "All time" option
  useEffect(() => {
    fetchEarliestDate().then(setEarliestDate).catch(err => {
      console.error('Error in TimeRangeSelector fetching earliestDate:', err)
      // Potentially set an error state or provide a fallback if needed
    })
  }, [])

  // Effect to synchronize internal selectedValue and showCustomControls with the `value` prop from parent
  useEffect(() => {
    setSelectedValue(value)
    setShowCustomControls(value === 'custom')
  }, [value])

  // Effect to populate custom date inputs if 'custom' mode is active
  useEffect(() => {
    if (value === 'custom') {
      const hasPropTimes = propInitialStartTime && propInitialEndTime
      const propTimesHaveChanged =
        hasPropTimes &&
        (propInitialStartTime !== prevPropInitialStartTime.current ||
         propInitialEndTime !== prevPropInitialEndTime.current)

      if (isExternallySetCustom && hasPropTimes) {
        setCustomStartTime(propInitialStartTime)
        setCustomEndTime(propInitialEndTime)
        if (onExternalCustomProcessed) {
          onExternalCustomProcessed()
        }
      } else if (propTimesHaveChanged) {
        setCustomStartTime(propInitialStartTime)
        setCustomEndTime(propInitialEndTime)
        // If the parent component isn't setting isExternallySetCustom=true but changes props,
        // this path handles it as a one-off. If onExternalCustomProcessed is meant to clear
        // a flag in the parent even in this scenario, it could be called here.
        // However, the primary expectation is that the parent uses isExternallySetCustom.
      } else if (!isExternallySetCustom && (!customStartTime || !customEndTime)) {
        const { startDate, endDate } = getDefaultCustomDateRange() // Typically "yesterday"
        const formattedStartDate = formatDateForInput(startDate)
        const formattedEndDate = formatDateForInput(endDate)
        // Set defaults only if fields are truly empty to avoid overwriting partially entered user data
        // if this effect were to run for other reasons.
        if (!customStartTime) setCustomStartTime(formattedStartDate)
        if (!customEndTime) setCustomEndTime(formattedEndDate)
      } else {
        // Preserving current custom times (user input or stable state).
      }
    } else { // value is NOT 'custom' (e.g., 'last24Hours', 'allTime', etc.)
      if (customStartTime || customEndTime) { // Only clear if they are currently set
        setCustomStartTime('')
        setCustomEndTime('')
      } else {
        // Custom times already clear. No change.
      }
    }

    // Update refs for the next render AFTER all logic has run
    prevPropInitialStartTime.current = propInitialStartTime
    prevPropInitialEndTime.current = propInitialEndTime

  }, [
    value,
    isExternallySetCustom,
    propInitialStartTime,
    propInitialEndTime,
    customStartTime,
    customEndTime,
    onExternalCustomProcessed,
  ])

  const timeRanges = useMemo(() => {
    const allTimeOption = [{ label: 'All time', value: 'allTime' }]
    const standardRanges = [
      { label: '5 minutes', value: 300 },
      { label: '15 minutes', value: 900 },
      { label: '1 hour', value: 3600 },
    ]
    const extendedRanges = [
      { label: '6 hours', value: 21600 },
      { label: '12 hours', value: 43200 },
      { label: '24 hours', value: 86400 },
      { label: '3 days', value: 259200 },
      { label: '7 days', value: 604800 },
    ]
    const customOption = [{ label: 'Custom Range...', value: 'custom' }]
    return includeExtendedRanges
      ? [...allTimeOption, ...standardRanges, ...extendedRanges, ...customOption]
      : [...allTimeOption, ...standardRanges, ...customOption]
  }, [includeExtendedRanges])

  const handleChange = useCallback(async e => {
    const newValue = e.target.value
    // The parent's `value` prop will update via `onChange`,
    // then the useEffects above will synchronize local state (selectedValue, showCustomControls, and custom inputs).

    if (newValue === 'custom') {
      onChange({ duration: 'custom' }) // Inform parent to switch to custom mode.
                                        // useEffect will handle populating default dates if needed.
      return
    }

    if (newValue === 'allTime') {
      if (earliestDate && !isNaN(earliestDate.getTime())) {
        onChange(await createAllTimeRange(earliestDate))
      } else {
        console.error('Earliest date is invalid or not yet available for "All time":', earliestDate)
        onChange({ duration: 'allTime', error: 'Earliest date not available' })
      }
      return
    }
    // For preset durations (numbers in seconds)
    onChange(await createTimeRange(parseInt(newValue, 10)))
  }, [earliestDate, onChange])

  const handleCustomTimeSubmit = useCallback(e => {
    e.preventDefault()
    try {
      const parseDateString = str => {
        // datetime-local input values are directly parsable by new Date()
        const date = new Date(str)
        if (isNaN(date.getTime())) throw new Error('Invalid date format')
        return date
      }
      const startTime = parseDateString(customStartTime)
      const endTime = parseDateString(customEndTime)

      if (endTime <= startTime) {
        alert('End time must be after start time.') // Basic validation
        return
      }
      // Call onChange with the absolute start/end times and explicitly set duration to 'custom'
      onChange({ startTime, endTime, duration: 'custom' })
    } catch (error) {
      console.error('Error parsing custom date range:', error)
      alert('Invalid date format. Please check your dates and try again.')
    }
  }, [customStartTime, customEndTime, onChange])

  const handleResetToAllTime = useCallback(async () => {
    if (earliestDate && !isNaN(earliestDate.getTime())) {
      onChange(await createAllTimeRange(earliestDate))
    } else {
      console.error('Earliest date is invalid or not yet available for "All time" on reset:', earliestDate)
      // Fallback or notify: inform parent about the issue
      onChange({ duration: 'allTime', error: 'Earliest date not available for reset' })
    }
  }, [earliestDate, onChange]) // Removed createAllTimeRange from dependency array

  return (
    <div className="time-range-selector d-flex align-items-center flex-wrap flex-lg-nowrap gap-3"> {/* Allow wrap by default, nowrap on lg+ screens */}
      <div className="d-flex align-items-center">
        <label htmlFor="timeRangeSelect" className="form-label me-2 mb-0 fw-bold">Time Range:</label>
        <select
          id="timeRangeSelect"
          className="form-select w-auto" // Added w-auto
          value={selectedValue} // Controlled by selectedValue state, which syncs with parent's `value` prop
          onChange={handleChange}
          aria-label="Select time range"
        >
          {timeRanges.map(range => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
        {selectedValue === 'custom' && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={handleResetToAllTime}
            title="Reset view to show all time"
          >
            Show All Time
          </button>
        )}
      </div>
      {showCustomControls && (
        <div className="custom-time-controls p-3 border rounded bg-light d-inline-block"> {/* Removed mt-3 */}
          <form onSubmit={handleCustomTimeSubmit} className="d-flex flex-wrap flex-md-nowrap align-items-center gap-2" aria-labelledby="custom-range-heading"> {/* ADDED aria-labelledby */} 
            <h6 id="custom-range-heading" className="visually-hidden">Custom Time Range Controls</h6> {/* ADDED heading for aria-labelledby */} 
            <label htmlFor="customStartTime" className="form-label mb-0">Start Time:</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm w-auto"
              id="customStartTime"
              value={customStartTime} // Controlled by customStartTime state
              onChange={e => setCustomStartTime(e.target.value)}
              required
              aria-label="Custom start time" // ADDED aria-label
            />
            <label htmlFor="customEndTime" className="form-label mb-0">End Time:</label>
            <input
              type="datetime-local"
              className="form-control form-control-sm w-auto"
              id="customEndTime"
              value={customEndTime} // Controlled by customEndTime state
              onChange={e => setCustomEndTime(e.target.value)}
              required
              aria-label="Custom end time" // ADDED aria-label
            />
            <button type="submit" className="btn btn-primary btn-sm">Apply</button>
          </form>
        </div>
      )}
    </div>
  )
}

export default TimeRangeSelector